import type { JSX } from 'react'
import { AdSlot } from '../../components/AdSlot'
import { CollectedOverlay } from '../../components/CollectedOverlay'
import { ConfigurationTab } from '../../components/ConfigurationTab'
import { FlipBook } from '../../components/FlipBook'
import { HistoryView } from '../../components/HistoryView'
import { MetricsBar } from '../../components/MetricsBar'
import { StickerGrid } from '../../components/StickerGrid'
import { StickerModal } from '../../components/StickerModal'
import type { AlbumRecord, InvestmentDestination } from '../../../../core/domain'
import type { ThemeDefinition } from '../../../../core/domain'

interface AlbumWorkspaceViewProps {
  activeRecord: AlbumRecord
  albumPageSize: number
  availableAlbumPageSizeOptions: readonly number[]
  stickerNumbers: number[]
  stickerView: 'album' | 'flipbook'
  middleFrameTab: 'album' | 'history' | 'configuration'
  selectedSticker: number | null
  selectedDestinationId: string
  comments: string
  isTargetVisible: boolean
  justCollectedSticker: number | null
  theme: ThemeDefinition
  themeName: string
  themeHero: string
  middleFrameTabs: JSX.Element
  onSetStickerView: (value: 'album' | 'flipbook') => void
  onSetAlbumPageSize: (value: number) => void
  onSelectSticker: (stickerNumber: number | null) => void
  onBackToSelector: () => void
  onToggleTarget: () => void
  onUpdateAlbum: <K extends keyof AlbumRecord['config']>(
    key: K,
    value: AlbumRecord['config'][K]
  ) => void
  onAddDestination: (dest: InvestmentDestination) => void
  onRemoveDestination: (destId: string) => void
  onCloseModal: () => void
  onSaveStickerInvestment: () => void
  onResetStickerInvestment: (stickerNumber: number) => void
  onCommentsChange: (value: string) => void
  onDestinationChange: (value: string) => void
  onOpenExternalLink: (url: string) => void
  onCloseCollectedOverlay: () => void
  onGoToConfig: () => void
}

export function AlbumWorkspaceView({
  activeRecord,
  albumPageSize,
  availableAlbumPageSizeOptions,
  stickerNumbers,
  stickerView,
  middleFrameTab,
  selectedSticker,
  selectedDestinationId,
  comments,
  isTargetVisible,
  justCollectedSticker,
  theme,
  themeName,
  themeHero,
  middleFrameTabs,
  onSetStickerView,
  onSetAlbumPageSize,
  onSelectSticker,
  onBackToSelector,
  onToggleTarget,
  onUpdateAlbum,
  onAddDestination,
  onRemoveDestination,
  onCloseModal,
  onSaveStickerInvestment,
  onResetStickerInvestment,
  onCommentsChange,
  onDestinationChange,
  onOpenExternalLink,
  onCloseCollectedOverlay,
  onGoToConfig
}: AlbumWorkspaceViewProps): JSX.Element {
  const t = {
    appName: 'InvestAlbum',
    stickerAlbum: 'Sticker album',
    collectByInvesting: 'Collect by investing',
    browsePageByPage: 'Browse page by page',
    clickToRegister: 'Click to register',
    clickToInvest: 'Click to invest',
    tabAlbum: 'Album',
    tabFlipbook: 'Flipbook',
    tabHistory: 'History',
    tabConfiguration: 'Configuration',
    myInvestmentAlbum: 'My investment album',
    albumSizeLabel: 'Album size',
    stickersLabel: 'stickers',
    themeLabel: 'Theme',
    allAlbums: 'All albums'
  } as const

  return (
    <>
      <header className="hero-panel">
        <div>
          <p className="eyebrow">{t.appName}</p>
          <h1>{activeRecord.config.name}</h1>
          <p className="hero-copy">{themeHero}</p>
        </div>
        <div className="hero-meta">
          <div className="hero-chip">
            <span>{t.albumSizeLabel}</span>
            <strong>
              {activeRecord.config.size} {t.stickersLabel}
            </strong>
          </div>
          <div className="hero-chip">
            <span>{t.themeLabel}</span>
            <strong>{themeName}</strong>
          </div>
          <button className="ghost-button back-button" type="button" onClick={onBackToSelector}>
            <span className="back-button-arrow" aria-hidden="true">
              ←
            </span>
            <span>{t.allAlbums}</span>
          </button>
        </div>
      </header>

      <main className="content-grid">
        <section className="album-panel">
          <div className="panel-heading">
            <div>
              <p className="eyebrow">{t.stickerAlbum}</p>
              <h2>{stickerView === 'album' ? t.collectByInvesting : t.browsePageByPage}</h2>
            </div>
            <div className="panel-heading-actions">
              <div className="panel-view-switch" role="tablist" aria-label={t.stickerAlbum}>
                <button
                  className={stickerView === 'album' ? 'tab is-active' : 'tab'}
                  onClick={() => onSetStickerView('album')}
                  type="button"
                  role="tab"
                  aria-selected={stickerView === 'album'}
                >
                  {t.tabAlbum}
                </button>
                <button
                  className={stickerView === 'flipbook' ? 'tab is-active' : 'tab'}
                  onClick={() => onSetStickerView('flipbook')}
                  type="button"
                  role="tab"
                  aria-selected={stickerView === 'flipbook'}
                >
                  {t.tabFlipbook}
                </button>
              </div>
              <p className="panel-note">
                {stickerView === 'album' ? t.clickToRegister : t.clickToInvest}
              </p>
            </div>
          </div>

          {stickerView === 'album' ? (
            <StickerGrid
              stickerNumbers={stickerNumbers}
              stickers={activeRecord.stickers}
              theme={theme}
              album={activeRecord.config}
              stickersPerPage={albumPageSize}
              pageSizeOptions={availableAlbumPageSizeOptions}
              onPageSizeChange={(pageSize) => onSetAlbumPageSize(pageSize)}
              onStickerClick={onSelectSticker}
            />
          ) : (
            <FlipBook
              stickerNumbers={stickerNumbers}
              stickers={activeRecord.stickers}
              theme={theme}
              album={activeRecord.config}
              onStickerClick={onSelectSticker}
            />
          )}
        </section>

        {middleFrameTab === 'album' && (
          <section className="configuration-panel middle-frame-panel">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">{t.myInvestmentAlbum}</p>
                <h2>{t.stickerAlbum}</h2>
              </div>
              <div className="panel-heading-actions">{middleFrameTabs}</div>
            </div>
            <MetricsBar
              album={activeRecord.config}
              stickers={activeRecord.stickers}
              isTargetVisible={isTargetVisible}
              onToggleTarget={onToggleTarget}
            />
          </section>
        )}

        {middleFrameTab === 'history' && (
          <HistoryView
            album={activeRecord.config}
            stickers={activeRecord.stickers}
            headerActions={middleFrameTabs}
          />
        )}
        {middleFrameTab === 'configuration' && (
          <ConfigurationTab
            album={activeRecord.config}
            stickers={activeRecord.stickers}
            onUpdateAlbum={onUpdateAlbum as never}
            onAddDestination={onAddDestination}
            onRemoveDestination={onRemoveDestination}
            headerActions={middleFrameTabs}
          />
        )}
      </main>

      <section className="configuration-panel support-strip">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Support</p>
            <h2>Sponsored content and community support</h2>
          </div>
        </div>
        <AdSlot
          fallback={
            <div className="ad-banner-placeholder">This frame contains sponsored content.</div>
          }
        />
        <div className="support-actions">
          <button
            className="ghost-button"
            type="button"
            onClick={() => onOpenExternalLink('https://www.patreon.com/')}
          >
            Patreon
          </button>
          <button
            className="ghost-button"
            type="button"
            onClick={() => onOpenExternalLink('https://www.buymeacoffee.com/')}
          >
            Buy Me a Coffee
          </button>
        </div>
      </section>

      {selectedSticker !== null && (
        <StickerModal
          selectedSticker={selectedSticker}
          stickers={activeRecord.stickers}
          album={activeRecord.config}
          theme={theme}
          selectedDestinationId={selectedDestinationId}
          comments={comments}
          onDestinationChange={onDestinationChange}
          onCommentsChange={onCommentsChange}
          onSave={onSaveStickerInvestment}
          onReset={() => onResetStickerInvestment(selectedSticker)}
          onClose={onCloseModal}
          onGoToConfig={onGoToConfig}
          onOpenExternalLink={onOpenExternalLink}
        />
      )}

      {justCollectedSticker !== null && (
        <CollectedOverlay
          stickerNumber={justCollectedSticker}
          albumSize={activeRecord.config.size}
          theme={theme}
          currency={activeRecord.config.currency}
          onOpenExternalLink={onOpenExternalLink}
          onClose={onCloseCollectedOverlay}
        />
      )}
    </>
  )
}
